import { Injectable } from '@angular/core';
import pdfMake from 'pdfmake/build/pdfmake';
import { TDocumentDefinitions } from 'pdfmake/interfaces';

@Injectable({
  providedIn: 'root'
})
export class PdfMakeService {
  private fontsLoaded = false;
  private defaultFont = 'Roboto';
  private readonly maxPdfNumber = 10000;
  private readonly numericContextKeys = new Set([
    'margin',
    'marginLeft',
    'marginTop',
    'marginRight',
    'marginBottom',
    'pageMargins',
    'width',
    'height',
    'fontSize',
    'lineHeight',
    'leadingIndent',
    'characterSpacing',
    'columnGap',
    'columnWidth'
  ]);

  async ensureFonts(): Promise<string> {
    if (this.fontsLoaded) {
      return this.defaultFont;
    }

    const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
    const pdfFonts = (pdfFontsModule as any).default ?? pdfFontsModule;
    const vfsHost = (pdfFonts as any).pdfMake;
    if (!vfsHost?.vfs) {
      throw new Error('[pdfMake] vfs が読み込めませんでした。pdfmake の依存関係を確認してください。');
    }

    (pdfMake as any).vfs = vfsHost.vfs;
    (pdfMake as any).fonts = {
      Roboto: {
        normal: 'Roboto-Regular.ttf',
        bold: 'Roboto-Medium.ttf',
        italics: 'Roboto-Italic.ttf',
        bolditalics: 'Roboto-MediumItalic.ttf'
      }
    };

    try {
      const response = await fetch('assets/fonts/ipam.ttf');
      if (response.ok) {
        const buffer = await response.arrayBuffer();
        const base64 = this.arrayBufferToBase64(buffer);
        (pdfMake as any).vfs['ipam.ttf'] = base64;
        (pdfMake as any).fonts['IPA'] = {
          normal: 'ipam.ttf',
          bold: 'ipam.ttf',
          italics: 'ipam.ttf',
          bolditalics: 'ipam.ttf'
        };
        this.defaultFont = 'IPA';
      } else {
        console.warn('[pdfMake] assets/fonts/ipam.ttf が見つかりません。Roboto フォントで代替します。');
      }
    } catch (error) {
      console.warn('[pdfMake] ipam.ttf の読み込みに失敗しました。Roboto フォントで代替します。', error);
    }

    this.fontsLoaded = true;
    return this.defaultFont;
  }

  createPdf(docDefinition: TDocumentDefinitions) {
    const sanitized = this.sanitizeDocDefinition(docDefinition);
    return pdfMake.createPdf(sanitized);
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private sanitizeDocDefinition<T>(node: T, trail: string[] = []): T {
    if (node === null || node === undefined) {
      return node;
    }

    if (Array.isArray(node)) {
      for (let index = 0; index < node.length; index++) {
        node[index] = this.sanitizeDocDefinition(node[index], trail.concat(`[${index}]`));
      }
      return node;
    }

    if (node instanceof Date || typeof node === 'function') {
      return node;
    }

    if (typeof node === 'number') {
      return this.normalizeNumber(node, trail) as unknown as T;
    }

    if (typeof node === 'string') {
      const contextKey = this.getContextKey(trail);
      if (this.numericContextKeys.has(contextKey)) {
        const segments = node.split(/[\s,]+/).filter(Boolean);
        if (segments.length > 1) {
          const parsedSegments = segments.map(value => this.parseNumericString(value));
          if (parsedSegments.every((value): value is number => value !== undefined)) {
            return this.sanitizeDocDefinition(parsedSegments as unknown as T, trail);
          }
        }

        const numeric = this.parseNumericString(node);
        if (numeric !== undefined) {
          return this.sanitizeDocDefinition(numeric as unknown as T, trail);
        }

        const fallback = this.getNumericFallback(contextKey);
        console.warn('[pdfMake] 数値化できない値を既定値に置き換えます。', trail.join('.'), node, '=>', fallback);
        return this.sanitizeDocDefinition(fallback as unknown as T, trail);
      }
    }

    if (typeof node === 'object') {
      const record = node as Record<string, unknown>;
      for (const key of Object.keys(record)) {
        record[key] = this.sanitizeDocDefinition(record[key], trail.concat(key));
      }
      return node;
    }

    return node;
  }

  private normalizeNumber(value: number, trail: string[]): number {
    if (!Number.isFinite(value)) {
      console.warn('[pdfMake] 非有限の数値を 0 に置き換えます。', trail.join('.'));
      return 0;
    }

    if (Math.abs(value) > this.maxPdfNumber) {
      const clamped = Math.sign(value) * this.maxPdfNumber;
      console.warn(`[pdfMake] 異常な数値 ${value} を検出し ${clamped} に丸めました。`, trail.join('.'));
      return clamped;
    }

    return Math.round(value * 1000) / 1000;
  }

  private getContextKey(trail: string[]): string {
    for (let index = trail.length - 1; index >= 0; index--) {
      const token = trail[index];
      if (!/^\[\d+\]$/.test(token)) {
        return token.replace(/\[\d+\]/g, '');
      }
    }
    return '';
  }

  private parseNumericString(value: string): number | undefined {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return 0;
    }

    const unitPattern = /^-?\d+(?:\.\d+)?(?:px|pt|em|rem|%)?$/i;
    if (unitPattern.test(trimmed)) {
      const parsed = parseFloat(trimmed);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    const direct = Number(trimmed);
    if (Number.isFinite(direct)) {
      return direct;
    }

    return undefined;
  }

  private getNumericFallback(contextKey: string): unknown {
    switch (contextKey) {
      case 'lineHeight':
        return 1;
      case 'fontSize':
        return 10;
      case 'pageMargins':
      case 'margin':
        return [0, 0, 0, 0];
      default:
        return 0;
    }
  }
}
